import { Form, router } from "@inertiajs/react"
import { Loader2, MapPinCheck, PencilLine } from "lucide-react"
import { type RefObject, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { OnboardingCountrySelect } from "@/components/onboarding/onboarding-country-select"
import {
  CONFIRM_ADDRESS_RELOAD_ONLY,
  useConfirmAddressRefusal,
} from "@/components/shipments/confirm-address-refusal"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useFocusOnRefusal } from "@/hooks/use-focus-on-refusal"
import { addressLines } from "@/lib/format-address"
import { plausiblePhone } from "@/lib/plausible-phone"
import { REFUSAL_FOCUS_ATTR, describedBy } from "@/lib/refusal-focus"
import { cn } from "@/lib/utils"
import { confirmBrandShipmentsAddressPath } from "@/routes"
import type { Locale } from "@/types"
import type {
  BrandAddressAction,
  ShipmentAddress,
  ShipmentContact,
} from "@/types/shipments"

/** One collab this dialog writes, with the version token it was rendered at. */
export interface ConfirmAddressTarget {
  id: number
  snapshotVersion: string
}

interface ConfirmAddressDialogProps {
  /** Every collab the submit writes — one for a Waiting row, N for a parcel. */
  targets: ConfirmAddressTarget[]
  /** `confirm` → « Confirmer l'adresse » (summary first); `edit` → fields. */
  action: BrandAddressAction
  creatorLabel: string
  /** ABSENT/undefined when nothing is on file — the dialog opens in edit mode. */
  address: ShipmentAddress | undefined
  contact: ShipmentContact | undefined
  locale: Locale
  /** Shipments queue placement (UX §2.1): primary on Waiting, outline/ghost on a parcel. */
  triggerVariant: "default" | "outline" | "ghost"
  testIdPrefix: string
}

type FieldName =
  | "recipient_name"
  | "street"
  | "street_line_2"
  | "postal_code"
  | "city"
  | "phone"

const TEXT_FIELDS: { name: FieldName; required: boolean }[] = [
  { name: "recipient_name", required: true },
  { name: "street", required: true },
  { name: "street_line_2", required: false },
  { name: "postal_code", required: true },
  { name: "city", required: true },
]

const ADDRESS_FIELDS = [
  "recipient_name",
  "street",
  "street_line_2",
  "postal_code",
  "city",
  "country",
] as const

/**
 * OKS-1037 — the brand confirms, or enters, the creator's shipping address
 * on their behalf (UX Spec §2.2).
 *
 * # ⚠️ `Dialog` at EVERY width (UX UQ1)
 *
 * Not a drawer under 768px: the Base UI Drawer's swipe capture swallows
 * non-button clicks, so focusing an input can fail. Full-screen styling under
 * 640px instead.
 *
 * # Two modes, ONE form, one POST
 *
 * `summary` shows what is on file read-only and posts it as HIDDEN inputs;
 * `edit` posts the typed fields. Either way the server writes exactly what the
 * operator saw, plus `snapshot_versions[<id>]` — the token that makes a
 * creator's newer edit refuse as `stale` instead of being silently reverted
 * (QA F6). The server decides everything; this component only renders.
 *
 * # Refusals stay in the dialog (OKS-1006)
 *
 * `errors.base` renders as a destructive `Alert` at the top and the footer
 * collapses to « Fermer », which reloads the queue props: every base refusal
 * (`not_revealable`, `already_confirmed`, `stale`) means the row moved under
 * the operator. Field errors render under their field. Never a toast.
 *
 * Inertia `<Form>` + plain `Input`/`Label` + `name` attributes. No RHF, no
 * zod, no shadcn `Form`.
 */
export function ConfirmAddressDialog({
  targets,
  action,
  creatorLabel,
  address,
  contact,
  locale,
  triggerVariant,
  testIdPrefix,
}: ConfirmAddressDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const initialMode = action === "confirm" && address ? "summary" : "edit"
  const [mode, setMode] = useState<"summary" | "edit">(initialMode)
  const bodyRef = useRef<HTMLDivElement>(null)

  const phoneOnFile = contact?.phone?.number ?? ""
  const phoneNeedsInput = !plausiblePhone(phoneOnFile)

  // OKS-1037 D1 — tell the page while this dialog is open, so a refusal that
  // arrives after the row has moved sections (and unmounted this dialog) is
  // still shown. See `confirm-address-refusal.tsx`.
  const refusal = useConfirmAddressRefusal()
  useEffect(() => {
    if (!open || !refusal) return
    return refusal.registerOpen()
  }, [open, refusal])

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) {
      setMode(initialMode)
      refusal?.settled()
    }
  }

  function closeAndReload() {
    handleOpenChange(false)
    router.reload({ only: CONFIRM_ADDRESS_RELOAD_ONLY })
  }

  const TriggerIcon = action === "confirm" ? MapPinCheck : PencilLine
  const triggerLabel =
    action === "confirm"
      ? t("shipments.confirm_address.trigger")
      : t("shipments.confirm_address.edit_trigger")

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant={triggerVariant}
            size="sm"
            className="max-sm:min-h-11 max-sm:w-full"
            // WCAG 2.5.3 label-in-name: the accessible name must contain the
            // visible label, so « Modifier l'adresse » gets its own aria.
            aria-label={
              action === "confirm"
                ? t("shipments.confirm_address.aria", { creator: creatorLabel })
                : t("shipments.confirm_address.edit_aria", {
                    creator: creatorLabel,
                  })
            }
            data-testid={`${testIdPrefix}-confirm-address-trigger`}
            data-action={action}
          >
            <TriggerIcon aria-hidden="true" />
            {triggerLabel}
          </Button>
        }
      />
      <DialogContent
        data-testid="shipments-confirm-address-dialog"
        data-mode={mode}
        className="max-sm:h-dvh max-sm:max-w-none max-sm:rounded-none sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>
            {t("shipments.confirm_address.title", { creator: creatorLabel })}
          </DialogTitle>
          <DialogDescription>
            {t("shipments.confirm_address.description")}
          </DialogDescription>
        </DialogHeader>

        <Form
          // ⚠️ `min-w-0`: `DialogContent` is a CSS grid, and a grid item's
          // default `min-width: auto` let the summary footer's three buttons
          // widen the track past `sm:max-w-md` (PO walk D2: 480 > 446px).
          className="min-w-0"
          action={confirmBrandShipmentsAddressPath()}
          method="post"
          options={{ preserveScroll: true }}
          // OKS-1037 D1 — a new attempt forgets the previous refusal; a base
          // refusal is handed to the page so it survives this dialog
          // unmounting when the row moves sections (see the provider).
          onStart={() => refusal?.settled()}
          onError={(errs) => {
            const raw = (errs as Record<string, string | string[]>).base
            const base = Array.isArray(raw) ? raw[0] : raw
            if (base) refusal?.refused(base)
          }}
          onSuccess={() => handleOpenChange(false)}
          data-testid="shipments-confirm-address-form"
        >
          {({ processing, errors }) => {
            const errorFor = (key: string): string | undefined => {
              const raw = (errors as Record<string, string | string[]>)?.[key]
              return Array.isArray(raw) ? raw[0] : raw
            }
            const baseError = errorFor("base")
            // A field error on a summary-mode submit (e.g. a stale profile
            // address missing a field) can only be fixed in edit mode.
            const hasFieldError = ADDRESS_FIELDS.some((f) => errorFor(f))
            const shownMode = hasFieldError ? "edit" : mode

            return (
              <div ref={bodyRef} className="flex flex-col gap-4">
                <FocusOnRefusal errors={errors} containerRef={bodyRef} />

                {targets.map((target) => (
                  <span key={target.id} hidden>
                    <input
                      type="hidden"
                      name="collab_ids[]"
                      value={target.id}
                    />
                    <input
                      type="hidden"
                      name={`snapshot_versions[${target.id}]`}
                      value={target.snapshotVersion}
                    />
                  </span>
                ))}

                {baseError && (
                  <Alert
                    variant="destructive"
                    role="alert"
                    tabIndex={-1}
                    {...{ [REFUSAL_FOCUS_ATTR]: "" }}
                    data-testid="shipments-confirm-address-refusal"
                  >
                    <AlertDescription>{baseError}</AlertDescription>
                  </Alert>
                )}

                {shownMode === "summary" && address ? (
                  <SummaryBody address={address} locale={locale} />
                ) : (
                  <EditFields
                    address={address}
                    locale={locale}
                    errorFor={errorFor}
                    processing={processing}
                  />
                )}

                <PhoneField
                  phoneOnFile={phoneOnFile}
                  asInput={shownMode === "edit" || phoneNeedsInput}
                  error={errorFor("phone")}
                  processing={processing}
                />

                <DialogFooter className="max-sm:flex-col-reverse max-sm:gap-2 sm:flex-wrap">
                  {baseError ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="max-sm:min-h-11 max-sm:w-full"
                      onClick={closeAndReload}
                      data-testid="shipments-confirm-address-close"
                    >
                      {t("shipments.confirm_address.close")}
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        className="max-sm:min-h-11 max-sm:w-full"
                        disabled={processing}
                        onClick={() =>
                          shownMode === "edit" &&
                          address &&
                          action === "confirm"
                            ? setMode("summary")
                            : handleOpenChange(false)
                        }
                        data-testid="shipments-confirm-address-cancel"
                      >
                        {t("shipments.confirm_address.cancel")}
                      </Button>
                      {shownMode === "summary" && (
                        <Button
                          type="button"
                          variant="link"
                          className="max-sm:min-h-11"
                          disabled={processing}
                          onClick={() => setMode("edit")}
                          data-testid="shipments-confirm-address-switch-edit"
                        >
                          {t("shipments.confirm_address.switch_to_edit")}
                        </Button>
                      )}
                      <Button
                        type="submit"
                        className="max-sm:min-h-11 max-sm:w-full"
                        disabled={processing}
                        data-testid="shipments-confirm-address-submit"
                      >
                        {processing && (
                          <Loader2
                            aria-hidden="true"
                            className="animate-spin"
                          />
                        )}
                        {processing
                          ? t("shipments.confirm_address.submitting")
                          : shownMode === "summary"
                            ? t("shipments.confirm_address.submit_confirm")
                            : t("shipments.confirm_address.submit_edit")}
                      </Button>
                    </>
                  )}
                </DialogFooter>
              </div>
            )
          }}
        </Form>
      </DialogContent>
    </Dialog>
  )
}

/** Summary mode: what is on file, read-only, posted as hidden inputs. */
function SummaryBody({
  address,
  locale,
}: {
  address: ShipmentAddress
  locale: Locale
}) {
  const { t } = useTranslation()

  return (
    <div
      className="bg-muted/40 flex flex-col gap-1 rounded-xl p-3"
      data-testid="shipments-confirm-address-summary"
    >
      <p className="text-muted-foreground text-xs font-medium">
        {address.origin === "snapshot"
          ? t("shipments.confirm_address.origin_snapshot")
          : t("shipments.confirm_address.origin_profile")}
      </p>
      {/* Long streets wrap, never truncate (UX §3). */}
      <address className="text-sm break-words not-italic">
        {addressLines(address, locale).map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </address>
      {ADDRESS_FIELDS.map((field) => (
        <input
          key={field}
          type="hidden"
          name={`shipping_address[${field}]`}
          value={address[field] ?? ""}
        />
      ))}
    </div>
  )
}

function EditFields({
  address,
  locale,
  errorFor,
  processing,
}: {
  address: ShipmentAddress | undefined
  locale: Locale
  errorFor: (key: string) => string | undefined
  processing: boolean
}) {
  const { t } = useTranslation()
  const countryError = errorFor("country")

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {TEXT_FIELDS.map(({ name, required }) => {
        const id = `confirm-address-${name}`
        const error = errorFor(name)
        const errorId = `${id}-error`
        const onFile = address?.[name as keyof ShipmentAddress] as
          | string
          | null
          | undefined
        return (
          <div
            key={name}
            className={cn(
              "flex flex-col gap-1",
              name !== "postal_code" && name !== "city" && "sm:col-span-2",
            )}
          >
            <Label htmlFor={id} className="text-xs">
              {t(`shipments.confirm_address.fields.${name}`)}
              {required && <RequiredMarker />}
            </Label>
            {/* ⚠️ No native `required` (Crew posture OKS-661/OKS-692): the
                browser's own bubble is unlocalized and blocks the submit, so
                the server's field error — rendered inline below — is the one
                message. `aria-required` + the « * » carry the requirement. */}
            <Input
              id={id}
              name={`shipping_address[${name}]`}
              defaultValue={onFile ?? ""}
              aria-required={required ? "true" : undefined}
              readOnly={processing}
              aria-invalid={error ? "true" : undefined}
              aria-describedby={describedBy(error && errorId)}
              className="max-sm:min-h-11"
              data-testid={`shipments-confirm-address-input-${name}`}
              // UX §4 — switching summary → edit moves focus to the first field.
              autoFocus={name === "recipient_name"}
            />
            {error && (
              <p id={errorId} role="alert" className="text-destructive text-xs">
                {error}
              </p>
            )}
          </div>
        )
      })}
      <div className="flex flex-col gap-1 sm:col-span-2">
        <Label htmlFor="confirm-address-country" className="text-xs">
          {t("shipments.confirm_address.fields.country")}
        </Label>
        <OnboardingCountrySelect
          name="shipping_address[country]"
          id="confirm-address-country"
          testId="shipments-confirm-address-input-country"
          defaultValue={address?.country ?? "FR"}
          errorMessages={countryError ? [countryError] : undefined}
          errorId="confirm-address-country-error"
          disabled={processing}
          locale={locale}
        />
        {countryError && (
          <p
            id="confirm-address-country-error"
            role="alert"
            className="text-destructive text-xs"
          >
            {countryError}
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Phone is REQUIRED in both modes (PRD OQ8). A plausible number on file is a
 * read-only line in summary mode (posted hidden); a missing or implausible one
 * is a required input in either mode — required by the server, announced by
 * `aria-required` + « * », never by the native attribute.
 */
function PhoneField({
  phoneOnFile,
  asInput,
  error,
  processing,
}: {
  phoneOnFile: string
  asInput: boolean
  error: string | undefined
  processing: boolean
}) {
  const { t } = useTranslation()
  const id = "confirm-address-phone"

  if (!asInput) {
    return (
      <p
        className="text-sm"
        data-testid="shipments-confirm-address-phone-summary"
      >
        {t("shipments.confirm_address.phone_line", { phone: phoneOnFile })}
        <input
          type="hidden"
          name="shipping_address[phone]"
          value={phoneOnFile}
        />
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs">
        {t("shipments.confirm_address.fields.phone")}
        <RequiredMarker />
      </Label>
      {/* ⚠️ No native `required` — see the address fields above. A blank
          phone is refused by `Collabs::ConfirmAddressForCreator#shape_errors`
          as a `phone` FIELD error (before any lock), rendered below. */}
      <Input
        id={id}
        name="shipping_address[phone]"
        type="tel"
        autoComplete="tel"
        defaultValue={phoneOnFile}
        aria-required="true"
        readOnly={processing}
        maxLength={32}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy(`${id}-hint`, error && `${id}-error`)}
        className="max-sm:min-h-11"
        data-testid="shipments-confirm-address-input-phone"
      />
      <p id={`${id}-hint`} className="text-muted-foreground text-xs">
        {t("shipments.confirm_address.phone_hint")}
      </p>
      {error && (
        <p id={`${id}-error`} role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}
    </div>
  )
}

/** The visual « * » required marker; the requirement itself is `aria-required`. */
function RequiredMarker() {
  return (
    <span className="text-destructive ml-0.5" aria-hidden="true">
      *
    </span>
  )
}

function FocusOnRefusal({
  errors,
  containerRef,
}: {
  errors: Record<string, unknown> | undefined
  containerRef: RefObject<HTMLDivElement | null>
}) {
  useFocusOnRefusal(errors, containerRef)
  return null
}
